// 商行详情 — 针式打印模板配置（配货单、销售单）
import React, { PureComponent } from 'react';
import { Button, Modal, Table } from '@/components';
import { message, Popconfirm, Spin, Tag } from 'antd-v5';
import addIcon from '@/common/images/add.png';
import { TEMPLATE_SOURCE_TYPE, TEMPLATE_TYPE } from '../../../label-template-management/index';
import {
  requestNeedleOrderTemplateList,
  requestNeedleOrderTemplateDelete,
  requestNeedleOrderTemplateUpdateDefault,
  requestNeedleMemoTemplateView,
  requestNeedleMemoTemplatePreview,
  requestNeedleOrderTemplatePreview,
} from '../../../../state/request/tag';
import './stallDotMatrixPrintTemplate.less';
import {
  STALL_DOT_MATRIX_DISTRIBUTION_EDIT_PATH,
  STALL_DOT_MATRIX_SALES_TEMPLATE_EDIT_PATH,
  getPrintLayoutLabelFromRecord,
} from './stallDotMatrixPrintShared';
import { resolvePdfBlobFromResponse, revokeBlobPreviewUrl } from '../../../../utils/utils';

const DISTRIBUTION_ROW_KEY = 'distribution-memo';

/** 配货单接口单条数据 → 表格行 */
function buildDistributionListRow(data) {
  const templateBO = data?.templateBO;
  const record = {
    key: DISTRIBUTION_ROW_KEY,
    templateName: '标准模板',
    templateType: '系统预设',
    templateBO,
    cxmJolimarkTypeEnum: templateBO?.printStyle,
  };
  record.printLayout = getPrintLayoutLabelFromRecord(record);
  return record;
}

function getTemplateSourceLabel(sourceType) {
  const item = TEMPLATE_TYPE.find((t) => t.value === sourceType);
  return (item && item.label) || '-';
}

const LINK_PREVENT = (fn) => (e) => {
  e.preventDefault();
  fn();
};

// 上下两张表共用列宽，保证表头与列对齐一致
const MATRIX_TABLE_SCROLL_X = 1120;
const COL_W = {
  index: 72,
  templateName: 400,
  printLayout: 200,
  templateType: 128,
  action: 280,
};

const TEMPLATE_NAME_MAX = 32;

/** 复制：基于列表项构造新增页初始数据（不含 id） */
function buildCopyRecordFromListItem(record) {
  const baseName = (record.templateName || '模板').trim();
  const templateName = `${baseName}（副本）`.slice(0, TEMPLATE_NAME_MAX);
  return {
    templateName,
    templateDetail: record.templateDetail,
    cxmJolimarkTypeEnum: record.cxmJolimarkTypeEnum,
  };
}

class StallDotMatrixPrintTemplate extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      distributionList: [],
      distributionListLoading: false,
      salesList: [],
      salesListLoading: false,
      previewOpen: false,
      previewTitle: '',
      previewPdfUrl: '',
      previewLoading: false,
    };
  }

  componentDidMount() {
    this.fetchDistributionTemplate();
    this.fetchSalesTemplateList();
  }

  componentWillUnmount() {
    revokeBlobPreviewUrl(this.state.previewPdfUrl);
  }

  revokeCurrentPreviewUrl = () => {
    revokeBlobPreviewUrl(this.state.previewPdfUrl);
  };

  fetchDistributionTemplate = async () => {
    const { stallId } = this.props;
    if (!stallId) return;
    this.setState({ distributionListLoading: true });
    try {
      const res = await requestNeedleMemoTemplateView({ stallId });
      if (res?.code === 200) {
        const row = buildDistributionListRow(res.data);
        this.setState({
          distributionList: row.templateBO ? [row] : [],
          distributionListLoading: false,
        });
      } else {
        this.setState({ distributionListLoading: false });
      }
    } catch {
      this.setState({ distributionListLoading: false });
    }
  };

  fetchSalesTemplateList = async () => {
    const { stallId } = this.props;
    if (!stallId) return;
    this.setState({ salesListLoading: true });
    try {
      const res = await requestNeedleOrderTemplateList({ stallId });
      if (res?.code === 200) {
        this.setState({ salesList: res.data || [], salesListLoading: false });
      } else {
        this.setState({ salesListLoading: false });
      }
    } catch {
      this.setState({ salesListLoading: false });
    }
  };

  goDistributionEdit = (record) => {
    const { stallId, marketId, history } = this.props;
    if (!history || !stallId) {
      message.error('无法跳转，请刷新页面重试');
      return;
    }
    history.push({
      pathname: STALL_DOT_MATRIX_DISTRIBUTION_EDIT_PATH,
      state: {
        stallId,
        marketId,
        templateBO: record.templateBO,
        tab: '18',
      },
    });
  };

  goSalesTemplateEdit = (record, mode = 'edit') => {
    const { stallId, marketId, history } = this.props;
    if (!history || !stallId) {
      message.error('无法跳转，请刷新页面重试');
      return;
    }
    if (mode === 'edit' && record?.sourceType === TEMPLATE_SOURCE_TYPE.SYSTEM) {
      message.warning('系统预设模板不能编辑');
      return;
    }
    history.push({
      pathname: STALL_DOT_MATRIX_SALES_TEMPLATE_EDIT_PATH,
      state: {
        stallId,
        marketId,
        mode,
        record: record || undefined,
        templateId: mode === 'edit' && record ? record.id : undefined,
        tab: '18',
      },
    });
  };

  goSalesTemplateCopy = (record) => {
    if (!record?.templateDetail) {
      message.error('无法复制，模板数据不完整');
      return;
    }
    this.goSalesTemplateEdit(buildCopyRecordFromListItem(record), 'add');
  };

  handleUpdateDefaultTemplate = async (record) => {
    const { stallId } = this.props;
    if (!record?.id || !stallId) {
      message.error('无法操作，缺少模板或商行信息');
      return;
    }
    try {
      const res = await requestNeedleOrderTemplateUpdateDefault({
        stallId,
        templateId: record.id,
      });
      if (res?.code === 200) {
        message.success('已设为默认模板');
        this.fetchSalesTemplateList();
      }
    } catch {}
  };

  handleTemplatePreview = async (params, previewApi, previewTitle) => {
    this.revokeCurrentPreviewUrl();
    this.setState({ previewOpen: true, previewTitle, previewLoading: true, previewPdfUrl: '' });
    try {
      const res = await previewApi(params);
      const pdfBlob = await resolvePdfBlobFromResponse(res);
      if (!pdfBlob) {
        this.setState({ previewOpen: false, previewLoading: false });
        return;
      }
      this.setState({
        previewPdfUrl: window.URL.createObjectURL(pdfBlob),
        previewLoading: false,
      });
    } catch {
      this.setState({ previewOpen: false, previewLoading: false });
    }
  };

  handleDistributionPreview = () => {
    const { stallId } = this.props;
    if (!stallId) {
      message.error('无法预览，缺少商行信息');
      return;
    }
    this.handleTemplatePreview({ stallId }, requestNeedleMemoTemplatePreview, '配货单预览');
  };

  handleSalesPreview = (record) => {
    if (!record?.id) {
      message.error('无法预览，缺少模板信息');
      return;
    }
    this.handleTemplatePreview({ id: record.id }, requestNeedleOrderTemplatePreview, '销售单预览');
  };

  closePreview = () => {
    this.revokeCurrentPreviewUrl();
    this.setState({
      previewOpen: false,
      previewTitle: '',
      previewPdfUrl: '',
      previewLoading: false,
    });
  };

  handleDeleteSalesTemplate = async (record) => {
    if (!record?.id) {
      message.error('无法删除，缺少模板信息');
      return;
    }
    if (record.defaultTemplateFlag) {
      message.warning('默认模板不能删除');
      return;
    }
    if (record.sourceType === TEMPLATE_SOURCE_TYPE.SYSTEM) {
      message.warning('系统预设模板不能删除');
      return;
    }
    try {
      const res = await requestNeedleOrderTemplateDelete(record.id);
      if (res?.code === 200) {
        message.success('删除成功');
        this.fetchSalesTemplateList();
      }
    } catch {}
  };

  getBaseColumns = () => [
    {
      title: '序号',
      key: 'index',
      width: COL_W.index,
      render: (_, __, index) => index + 1,
    },
    {
      title: '模板名称',
      dataIndex: 'templateName',
      key: 'templateName',
      width: COL_W.templateName,
    },
    {
      title: '打印样式',
      dataIndex: 'printLayout',
      key: 'printLayout',
      width: COL_W.printLayout,
    },
    {
      title: '类型',
      dataIndex: 'templateType',
      key: 'templateType',
      width: COL_W.templateType,
    },
  ];

  renderTemplateName = (_, record) => (
    <span className='templateNameCell'>
      <span className='templateNameText'>{record.templateName || '-'}</span>
      {record.defaultTemplateFlag ? (
        <Tag className='templateDefaultTag' color='#01b755'>
          默认
        </Tag>
      ) : null}
    </span>
  );

  getSalesColumns = () => [
    this.getBaseColumns()[0],
    {
      title: '模板名称',
      key: 'templateName',
      width: COL_W.templateName,
      className: 'templateNameColumn',
      render: this.renderTemplateName,
    },
    {
      title: '打印样式',
      key: 'printLayout',
      width: COL_W.printLayout,
      render: (_, record) => getPrintLayoutLabelFromRecord(record),
    },
    {
      title: '类型',
      key: 'templateType',
      width: COL_W.templateType,
      render: (_, record) => getTemplateSourceLabel(record.sourceType),
    },
  ];

  renderDistributionActions = (record) => (
    <span>
      <a
        href='/'
        className='linkAction'
        onClick={LINK_PREVENT(() => this.goDistributionEdit(record))}
      >
        编辑
      </a>
      <a
        href='/'
        className='linkAction'
        onClick={LINK_PREVENT(() => this.handleDistributionPreview())}
      >
        预览
      </a>
    </span>
  );

  renderSalesActions = (record) => {
    const isSystem = record.sourceType === TEMPLATE_SOURCE_TYPE.SYSTEM;
    const isDefault = !!record.defaultTemplateFlag;
    const cannotDelete = isSystem || isDefault;
    const disabledLinkStyle = { color: 'rgba(0, 0, 0, 0.25)', cursor: 'not-allowed' };
    const defaultLinkStyle = isDefault ? { ...disabledLinkStyle, marginRight: 16 } : undefined;

    return (
      <span>
        <a
          href='/'
          className='linkAction'
          style={isSystem ? disabledLinkStyle : undefined}
          onClick={LINK_PREVENT(() => {
            if (!isSystem) this.goSalesTemplateEdit(record, 'edit');
          })}
        >
          编辑
        </a>
        <a
          href='/'
          className='linkAction'
          onClick={LINK_PREVENT(() => this.handleSalesPreview(record))}
        >
          预览
        </a>
        <a
          href='/'
          className='linkAction'
          onClick={LINK_PREVENT(() => this.goSalesTemplateCopy(record))}
        >
          复制
        </a>
        {isDefault ? (
          <a
            href='/'
            className='linkAction'
            style={defaultLinkStyle}
            onClick={(e) => e.preventDefault()}
          >
            设置默认模板
          </a>
        ) : (
          <Popconfirm
            title='确定将该模板设为默认模板吗？'
            okText='确定'
            cancelText='取消'
            onConfirm={() => this.handleUpdateDefaultTemplate(record)}
          >
            <a href='/' className='linkAction' onClick={(e) => e.preventDefault()}>
              设置默认模板
            </a>
          </Popconfirm>
        )}
        <Popconfirm
          title='确定删除该模板吗？'
          okText='确定'
          cancelText='取消'
          disabled={cannotDelete}
          onConfirm={() => this.handleDeleteSalesTemplate(record)}
        >
          <a
            href='/'
            style={cannotDelete ? disabledLinkStyle : { color: '#F21717', cursor: 'pointer' }}
            onClick={(e) => e.preventDefault()}
          >
            删除
          </a>
        </Popconfirm>
      </span>
    );
  };

  render() {
    const {
      distributionList,
      distributionListLoading,
      salesList,
      salesListLoading,
      previewOpen,
      previewTitle,
      previewPdfUrl,
      previewLoading,
    } = this.state;

    const distributionColumns = [
      ...this.getBaseColumns(),
      {
        title: '操作',
        key: 'action',
        width: COL_W.action,
        render: (_, record) => this.renderDistributionActions(record),
      },
    ];

    const salesColumns = [
      ...this.getSalesColumns(),
      {
        title: '操作',
        key: 'action',
        width: COL_W.action,
        render: (_, record) => this.renderSalesActions(record),
      },
    ];

    const tableScroll = { x: MATRIX_TABLE_SCROLL_X };

    return (
      <div className='stallDotMatrixPrintTemplate'>
        <div className='section'>
          <div className='sectionHeader'>
            <div className='sectionTitle'>配货单</div>
          </div>
          <div className='matrixTableWrap'>
            <Table
              rowKey='key'
              columns={distributionColumns}
              dataSource={distributionList}
              loading={distributionListLoading}
              pagination={false}
              scroll={tableScroll}
            />
          </div>
        </div>

        <div className='section'>
          <div className='sectionHeader'>
            <div className='sectionTitle'>销售单</div>
            <Button type='primary' onClick={() => this.goSalesTemplateEdit(null, 'add')}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <img src={addIcon} style={{ width: 16, height: 16, marginRight: 4 }} alt='add' />
                添加模板
              </div>
            </Button>
          </div>
          <div className='matrixTableWrap'>
            <Table
              rowKey='id'
              columns={salesColumns}
              dataSource={salesList}
              loading={salesListLoading}
              scroll={tableScroll}
            />
          </div>
        </div>

        <Modal
          title={previewTitle || '预览'}
          open={previewOpen}
          footer={null}
          width={1300}
          onCancel={this.closePreview}
          destroyOnClose
        >
          <div className='salesPreviewBody'>
            <Spin spinning={previewLoading}>
              {previewPdfUrl ? (
                <iframe
                  className='salesPreviewPdf'
                  title={previewTitle || '预览'}
                  src={previewPdfUrl}
                />
              ) : null}
            </Spin>
          </div>
        </Modal>
      </div>
    );
  }
}

export default StallDotMatrixPrintTemplate;
